/**
 * BankFeedKPIStrip - Phase 3 refactor extraction.
 *
 * The six-card stats strip that sits below the Bank Feed page header.
 * Each card is clickable and navigates the parent into the matching tab/
 * sub-filter. Items needing attention (Review Needed > 0, Failed > 0,
 * Reconciliation needs_attention > 0) get a subtle LEFT accent bar so
 * they read as informational, not selected. Earlier versions used
 * `ring-2 ring-violet-400` etc. on the entire card which visually
 * mimicked a "selected tab" state - particularly noticeable on the
 * Reconciliation card and confused users into thinking they were on the
 * Reconciliation tab when they were actually on Dashboard.
 */
import React from 'react';
import { Card, CardContent } from '../ui/card';
import {
  Activity,
  CheckCircle2,
  Clock,
  FileUp,
  Scale,
} from 'lucide-react';

const STAT_CARD_BASE = 'cursor-pointer hover:shadow-md transition-shadow';

export const BankFeedKPIStrip = ({
  stats,
  reconSummary,
  activityCounts,
  uploadedStatements = [],
  onOpenCompleted,
  onOpenReviewNeeded,
  onOpenDuplicates,
  onOpenStatements,       // iter1241 · replaces onOpenFailed
  onOpenReconciliation,
}) => {
  // iter1241 · "Failed" stat replaced by "Statements". Failed txns
  // are always 0 across the platform (real per-row statuses are
  // completed / review_needed / voided / matched / duplicate / ...).
  // Users need to see how their books track against uploads instead.
  const statementCount = uploadedStatements.length;
  let lastStmtLabel = 'No statements yet';
  if (statementCount) {
    const latest = uploadedStatements
      .map(s => s.uploaded_at)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
    if (latest) {
      const d = new Date(latest);
      if (!isNaN(d.getTime())) {
        lastStmtLabel = `Last ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
      }
    }
  }
  return (
    <div className="grid grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4">
      {/* Total */}
      <Card className="border-gray-200">
        <CardContent className="p-2.5 lg:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] lg:text-sm truncate text-gray-600">Total</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="relative">
              {activityCounts?.bank_feeds > 0 && (
                <span
                  className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 z-10"
                  data-testid="new-total-badge"
                >
                  {activityCounts.bank_feeds > 99 ? '99+' : activityCounts.bank_feeds}
                </span>
              )}
              <div className="hidden lg:flex w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-gray-500" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Completed */}
      <Card
        className={`${STAT_CARD_BASE} border-emerald-200 bg-emerald-50`}
        onClick={onOpenCompleted}
        data-testid="stat-completed"
      >
        <CardContent className="p-2.5 lg:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] lg:text-sm truncate text-emerald-700">In Books</p>
              <p className="text-lg lg:text-2xl font-bold text-emerald-800">{stats.completed}</p>
            </div>
            <div className="hidden lg:flex w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Review Needed */}
      <Card
        className={`${STAT_CARD_BASE} border-amber-200 bg-amber-50 ${stats.review_needed > 0 ? 'border-l-4 border-l-amber-500 animate-pulse-soft' : ''}`}
        onClick={onOpenReviewNeeded}
        data-testid="stat-review-needed"
      >
        <CardContent className="p-2.5 lg:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] lg:text-sm truncate text-amber-700 font-medium">Review Needed</p>
              <p className="text-lg lg:text-2xl font-bold text-amber-800">{stats.review_needed}</p>
            </div>
            <div
              className="hidden lg:flex w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"
            >
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statements — replaces the always-zero "Failed" card. iter1241 */}
      <Card
        className={`${STAT_CARD_BASE} border-violet-200 bg-violet-50/50 ${statementCount > 0 ? 'border-l-4 border-l-violet-500' : ''}`}
        onClick={onOpenStatements}
        data-testid="stat-statements"
      >
        <CardContent className="p-2.5 lg:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] lg:text-sm truncate text-violet-700">Statements</p>
              <p className="text-lg lg:text-2xl font-bold text-violet-800" data-testid="stat-statements-count">{statementCount}</p>
              <p className="text-[10px] lg:text-xs text-violet-600 mt-0.5 truncate" data-testid="stat-statements-last">
                {lastStmtLabel}
              </p>
            </div>
            <div className="hidden lg:flex w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
              <FileUp className="w-5 h-5 text-violet-600" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reconciliation */}
      <Card
        className={`${STAT_CARD_BASE} ${
          reconSummary && reconSummary.needs_attention_count > 0
            ? 'border-violet-200 bg-violet-50 border-l-4 border-l-violet-500'
            : reconSummary?.total_accounts > 0
              ? 'border-emerald-200 bg-emerald-50'
              : 'border-gray-200'
        }`}
        onClick={onOpenReconciliation}
        data-testid="stat-reconciliation"
      >
        <CardContent className="p-2.5 lg:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-[11px] lg:text-sm truncate ${reconSummary?.needs_attention_count > 0 ? 'text-violet-700' : 'text-gray-600'}`}>Reconciliation</p>
              <p className={`text-lg lg:text-2xl font-bold ${reconSummary?.needs_attention_count > 0 ? 'text-violet-800' : 'text-gray-700'}`}>
                {reconSummary ? reconSummary.total_unmatched : '-'}
              </p>
            </div>
            <div className={`hidden lg:flex w-10 h-10 rounded-full flex items-center justify-center ${reconSummary?.needs_attention_count > 0 ? 'bg-violet-100' : 'bg-gray-100'}`}>
              <Scale className={`w-5 h-5 ${reconSummary?.needs_attention_count > 0 ? 'text-violet-600' : 'text-gray-500'}`} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BankFeedKPIStrip;
